import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SIGNUP_INTENT_FLAG_KEY } from "../auth/useOnboardingState";

// Login page: two OAuth buttons carrying `?mode=signin` so the
// orchestrator refuses to silently create accounts on miss (0024).
// New users get redirected here with `?signin_no_account=1` and
// nudged to the Signup page where they accept the TOS first.
//
// These are <a href> links to the orchestrator's OAuth start endpoint,
// NOT XHRs (cookie auth requires a top-level browser navigation; a fetch
// can't follow the 302-set-cookie chain Google/Microsoft + the orchestrator
// run through). The orchestrator validates returnTo against an allowlist
// (FRONTEND_ALLOWED_RETURN_PREFIXES on Cloud Run), so passing the SPA
// origin is safe — open-redirect closed in slice 0.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? window.location.origin;

export function Login() {
  const [params] = useSearchParams();
  const returnToParam = params.get("returnTo");
  // `?deleted=1` is a one-shot post-account-deletion flag set by
  // useDeleteAccount when it bounces here. We render it as a small inline
  // banner in lieu of a global toast system; reloading the page drops the
  // flag, which is fine — it's confirmation, not state.
  const justDeleted = params.get("deleted") === "1";
  // `?signin_no_account=1` is set by the orchestrator's OAuth callback
  // when the user clicked Sign-in with a provider account that doesn't
  // exist in our system (0024). Render the no-account banner; the user
  // clicks the Sign up link to start a real signup with TOS.
  const signinNoAccount = params.get("signin_no_account") === "1";

  // Clear any stale `pmt-signup-intent` flag on Login mount (0024 F10).
  // Signing in is the explicit "I'm not signing up" signal — clear the
  // signup-intent so a previously-abandoned signup can't accidentally
  // fire onboarding for a returning-user signin.
  useEffect(() => {
    try {
      window.localStorage.removeItem(SIGNUP_INTENT_FLAG_KEY);
    } catch {
      // Private mode / storage disabled — no-op.
    }
  }, []);

  // Default landing: SPA root. If a guarded route bounced the user here,
  // returnToParam holds the relative path; we forward that as-is.
  const returnTo = returnToParam
    ? `${window.location.origin}${returnToParam}`
    : `${window.location.origin}/`;

  const buildOAuthUrl = (provider: "google" | "microsoft") =>
    `${API_BASE}/api/v1/auth/oauth/${provider}/start?mode=signin&return_to=${encodeURIComponent(returnTo)}`;

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6">
      <div
        className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full opacity-20 blur-3xl"
        style={{
          background: "radial-gradient(circle, rgba(124,122,255,0.6), transparent 70%)",
        }}
      />

      <div className="relative w-full max-w-sm animate-slide-up">
        {justDeleted ? (
          <div
            className="mb-6 rounded-md border border-ink-700 bg-ink-900/60 px-3 py-2 text-xs text-ink-200"
            role="status"
          >
            Your account has been deleted.
          </div>
        ) : null}

        {signinNoAccount ? (
          // 0024: orchestrator's OAuth callback redirected here when the
          // user signed in with a provider account that has no record in
          // our system. Steer them to /signup where they accept TOS.
          <div
            className="mb-6 rounded-md border border-warn-solid/40 bg-warn-solid/10 px-3 py-2 text-xs text-warn-strong"
            role="status"
          >
            We didn't find an account for that sign-in.{" "}
            <Link
              to="/signup?from=signin-no-account"
              className="text-warn-strong hover:text-ink-50 underline-offset-2 underline"
            >
              Sign up
            </Link>{" "}
            to create one.
          </div>
        ) : null}

        <div className="mb-12">
          <div className="flex items-center gap-2 mb-1">
            <div className="h-1.5 w-1.5 rounded-full bg-accent" />
            <span className="font-mono text-xs text-ink-300 tracking-widest uppercase">
              Eminity
            </span>
          </div>
          <h1 className="text-4xl font-medium text-ink-50 tracking-tightest mt-6">Sign in</h1>
          <p className="mt-3 text-sm text-ink-300 leading-relaxed">
            Eminity turns a one-line idea into a build-ready spec
            through iterative LLM analysis. Sign in to start.
          </p>
        </div>

        <div className="space-y-2">
          <a
            href={buildOAuthUrl("google")}
            className="group w-full flex items-center justify-between px-4 py-3 bg-ink-900 border border-ink-700 hover:border-ink-500 rounded-md text-sm text-ink-100 transition-colors"
          >
            <span className="flex items-center gap-3">
              <GoogleMark />
              Continue with Google
            </span>
            <span className="text-ink-400 group-hover:text-ink-200 transition-colors">→</span>
          </a>

          <a
            href={buildOAuthUrl("microsoft")}
            className="group w-full flex items-center justify-between px-4 py-3 bg-ink-900 border border-ink-700 hover:border-ink-500 rounded-md text-sm text-ink-100 transition-colors"
          >
            <span className="flex items-center gap-3">
              <MicrosoftMark />
              Continue with Microsoft
            </span>
            <span className="text-ink-400 group-hover:text-ink-200 transition-colors">→</span>
          </a>
        </div>

        <p className="mt-10 text-xs text-ink-400 leading-relaxed">
          Don't have an account?{" "}
          <Link
            to="/signup"
            className="text-ink-200 hover:text-accent underline-offset-2 hover:underline"
          >
            Sign up
          </Link>
          .
        </p>

        <div className="mt-16 pt-6 border-t border-ink-800 flex items-center justify-between text-xs font-mono text-ink-400">
          <span>{import.meta.env.VITE_API_BASE_URL ?? "(dev)"}</span>
        </div>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4">
      <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.32z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.06H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.94l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.1 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4">
      <path fill="#F25022" d="M1 1h10v10H1z"/>
      <path fill="#7FBA00" d="M13 1h10v10H13z"/>
      <path fill="#00A4EF" d="M1 13h10v10H1z"/>
      <path fill="#FFB900" d="M13 13h10v10H13z"/>
    </svg>
  );
}
